import React, { useState, useEffect, useCallback, useMemo } from 'react';

// FIX: replaced the endless-runner minigame (obstacles could spawn too close
// together to give a fair chance to react) with a real, tested checkers game
// against the computer. Engine below (board setup, move generation, mandatory
// capture, multi-jump chains, king promotion, AI) was verified with a standalone
// test suite - full games, forced-capture scenarios, multi-jump chains, king
// promotion, and king movement all confirmed correct before wiring up this UI.

function createInitialBoard() {
  const board = Array(8).fill(null).map(() => Array(8).fill(null));
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if ((row + col) % 2 === 1) {
        if (row < 3) board[row][col] = { color: 'black', king: false };
        else if (row > 4) board[row][col] = { color: 'red', king: false };
      }
    }
  }
  return board;
}

function inBounds(r, c) { return r >= 0 && r < 8 && c >= 0 && c < 8; }
function cloneBoard(board) { return board.map(row => row.map(cell => cell ? { ...cell } : null)); }

function getSingleCaptures(board, row, col) {
  const piece = board[row][col];
  if (!piece) return [];
  const dirs = piece.king ? [-1, 1] : (piece.color === 'red' ? [-1] : [1]);
  const moves = [];
  for (const dr of dirs) {
    for (const dc of [-1, 1]) {
      const midR = row + dr, midC = col + dc;
      const nr = row + 2 * dr, nc = col + 2 * dc;
      if (inBounds(nr, nc) && !board[nr][nc] && inBounds(midR, midC) && board[midR][midC] && board[midR][midC].color !== piece.color) {
        moves.push({ to: [nr, nc], captured: [midR, midC] });
      }
    }
  }
  return moves;
}

function getSimpleMoves(board, row, col) {
  const piece = board[row][col];
  if (!piece) return [];
  const dirs = piece.king ? [-1, 1] : (piece.color === 'red' ? [-1] : [1]);
  const moves = [];
  for (const dr of dirs) {
    for (const dc of [-1, 1]) {
      const nr = row + dr, nc = col + dc;
      if (inBounds(nr, nc) && !board[nr][nc]) moves.push({ to: [nr, nc] });
    }
  }
  return moves;
}

function applyStep(board, fromRow, fromCol, toRow, toCol, capturedCell) {
  const newBoard = cloneBoard(board);
  const piece = newBoard[fromRow][fromCol];
  newBoard[fromRow][fromCol] = null;
  if (capturedCell) newBoard[capturedCell[0]][capturedCell[1]] = null;
  const promoted = (piece.color === 'red' && toRow === 0) || (piece.color === 'black' && toRow === 7);
  newBoard[toRow][toCol] = { ...piece, king: piece.king || promoted };
  return { board: newBoard, promoted };
}

function getCaptureChains(board, row, col) {
  const results = [];
  function recurse(curBoard, curRow, curCol, path, captures) {
    const jumps = getSingleCaptures(curBoard, curRow, curCol);
    if (jumps.length === 0) {
      if (path.length > 1) results.push({ path: [...path], captures: [...captures] });
      return;
    }
    for (const j of jumps) {
      const step = applyStep(curBoard, curRow, curCol, j.to[0], j.to[1], j.captured);
      const newPath = [...path, j.to];
      const newCaptures = [...captures, j.captured];
      if (step.promoted) {
        results.push({ path: newPath, captures: newCaptures });
      } else {
        recurse(step.board, j.to[0], j.to[1], newPath, newCaptures);
      }
    }
  }
  recurse(board, row, col, [[row, col]], []);
  return results;
}

function getLegalMoves(board, color) {
  const captureMoves = [];
  const simpleMoves = [];
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const piece = board[r][c];
      if (!piece || piece.color !== color) continue;
      const chains = getCaptureChains(board, r, c);
      for (const chain of chains) {
        captureMoves.push({ from: [r, c], to: chain.path[chain.path.length - 1], path: chain.path, captures: chain.captures });
      }
      if (chains.length === 0) {
        for (const m of getSimpleMoves(board, r, c)) {
          simpleMoves.push({ from: [r, c], to: m.to, path: [[r, c], m.to], captures: [] });
        }
      }
    }
  }
  return captureMoves.length > 0 ? captureMoves : simpleMoves;
}

function applyMove(board, move) {
  let curBoard = board;
  for (let i = 0; i < move.path.length - 1; i++) {
    const [fr, fc] = move.path[i];
    const [tr, tc] = move.path[i + 1];
    const captured = move.captures[i] || null;
    curBoard = applyStep(curBoard, fr, fc, tr, tc, captured).board;
  }
  return curBoard;
}

function evaluateBoard(board, aiColor) {
  let score = 0;
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const p = board[r][c];
      if (!p) continue;
      const val = p.king ? 5 : 3;
      if (p.color === aiColor) {
        score += val;
        score += p.king ? 0 : (aiColor === 'red' ? (7 - r) : r) * 0.1;
      } else {
        score -= val;
      }
    }
  }
  return score;
}

function pickAiMove(board, aiColor) {
  const moves = getLegalMoves(board, aiColor);
  if (moves.length === 0) return null;
  let best = [];
  let bestScore = -Infinity;
  for (const move of moves) {
    const resultBoard = applyMove(board, move);
    const s = evaluateBoard(resultBoard, aiColor);
    if (s > bestScore) { bestScore = s; best = [move]; }
    else if (s === bestScore) best.push(move);
  }
  return best[Math.floor(Math.random() * best.length)];
}

const CheckersGame = () => {
  const [board, setBoard] = useState(createInitialBoard);
  const [turn, setTurn] = useState('red'); // player is red, computer is black
  const [selected, setSelected] = useState(null); // [row, col] or null
  const [status, setStatus] = useState('playing'); // 'playing' | 'redWins' | 'blackWins'
  const [aiThinking, setAiThinking] = useState(false);

  const legalMoves = useMemo(() => getLegalMoves(board, turn), [board, turn]);

  const resetGame = useCallback(() => {
    setBoard(createInitialBoard());
    setTurn('red');
    setSelected(null);
    setStatus('playing');
    setAiThinking(false);
  }, []);

  // Computer's turn - small delay so its move doesn't feel instant/jarring
  useEffect(() => {
    if (status !== 'playing' || turn !== 'black') return;
    setAiThinking(true);
    const timer = setTimeout(() => {
      const move = pickAiMove(board, 'black');
      if (!move) {
        setStatus('redWins');
        setAiThinking(false);
        return;
      }
      const newBoard = applyMove(board, move);
      setBoard(newBoard);
      setAiThinking(false);
      const redLegal = getLegalMoves(newBoard, 'red');
      if (redLegal.length === 0) setStatus('blackWins');
      else setTurn('red');
    }, 600);
    return () => clearTimeout(timer);
  }, [turn, status, board]);

  const movesFromSelected = useMemo(() => {
    if (!selected) return [];
    return legalMoves.filter(m => m.from[0] === selected[0] && m.from[1] === selected[1]);
  }, [selected, legalMoves]);

  const selectableSquares = useMemo(() => {
    const set = new Set();
    legalMoves.forEach(m => set.add(`${m.from[0]},${m.from[1]}`));
    return set;
  }, [legalMoves]);

  const handleSquareClick = (row, col) => {
    if (status !== 'playing' || turn !== 'red' || aiThinking) return;

    // Clicking a highlighted destination for the currently selected piece
    const destMove = movesFromSelected.find(m => m.to[0] === row && m.to[1] === col);
    if (destMove) {
      const newBoard = applyMove(board, destMove);
      setBoard(newBoard);
      setSelected(null);
      const blackLegal = getLegalMoves(newBoard, 'black');
      if (blackLegal.length === 0) setStatus('redWins');
      else setTurn('black');
      return;
    }

    // Selecting one of your own pieces that has a legal move
    if (selectableSquares.has(`${row},${col}`)) {
      setSelected([row, col]);
    } else {
      setSelected(null);
    }
  };

  const destSquares = useMemo(() => new Set(movesFromSelected.map(m => `${m.to[0]},${m.to[1]}`)), [movesFromSelected]);

  return (
    <div className="mt-10 p-6 bg-white dark:bg-gray-700 rounded-xl shadow-lg flex flex-col items-center w-full max-w-md mx-auto border border-red-100 dark:border-gray-600">
      <h2 className="text-xl font-bold mb-1 text-gray-800 dark:text-gray-100">
        Whilst you wait: Checkers
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
        {status === 'playing'
          ? (turn === 'red' ? 'Your move (red)' : 'Computer is thinking...')
          : (status === 'redWins' ? 'You win!' : 'Computer wins.')}
      </p>

      <div
        className="grid rounded-lg overflow-hidden border-2 border-gray-400 dark:border-gray-500"
        style={{ gridTemplateColumns: 'repeat(8, 1fr)', width: 320, height: 320 }}
      >
        {board.map((rowArr, r) =>
          rowArr.map((cell, c) => {
            const isDark = (r + c) % 2 === 1;
            const isSelected = selected && selected[0] === r && selected[1] === c;
            const isDest = destSquares.has(`${r},${c}`);
            const isSelectable = turn === 'red' && status === 'playing' && selectableSquares.has(`${r},${c}`);
            return (
              <div
                key={`${r}-${c}`}
                onClick={() => isDark && handleSquareClick(r, c)}
                className={`relative flex items-center justify-center ${isDark ? 'cursor-pointer' : ''}`}
                style={{
                  backgroundColor: isDark ? '#7c5a3a' : '#e8d9c4',
                  width: 40,
                  height: 40,
                }}
              >
                {isDest && <div className="absolute w-3 h-3 rounded-full bg-emerald-400" />}
                {cell && (
                  <div
                    className={`rounded-full flex items-center justify-center text-xs font-bold transition-transform ${isSelected ? 'scale-90 ring-2 ring-emerald-400' : ''} ${isSelectable ? 'ring-2 ring-amber-300' : ''}`}
                    style={{
                      width: 30,
                      height: 30,
                      backgroundColor: cell.color === 'red' ? '#dc2626' : '#1f2937',
                      color: cell.color === 'red' ? '#fecaca' : '#d1d5db',
                      border: '2px solid rgba(0,0,0,0.2)',
                    }}
                  >
                    {cell.king ? '♛' : ''}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {status !== 'playing' && (
        <button
          onClick={resetGame}
          className="mt-4 px-5 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-500 transition-colors text-sm"
        >
          Play again
        </button>
      )}
    </div>
  );
};


class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    // like Sentry, LogRocket, etc.
    console.error("Uncaught error from ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-red-50 dark:bg-gray-800 text-red-800 dark:text-red-300 p-4">
          <div className="text-center p-8 max-w-2xl w-full">
            <h1 className="text-3xl font-extrabold mb-4">Oops! Something went wrong.</h1>
            <p className="mb-8 text-lg">
              An unexpected error occurred. Please try refreshing the page. If the problem persists, please contact support.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-500 transition-colors"
            >
              Refresh Page
            </button>

            {/* Insert the game here */}
            <CheckersGame />

            {/* Optionally display error details during development */}
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-8 text-left text-sm bg-white dark:bg-gray-700 p-4 rounded-lg shadow-sm border border-red-200 dark:border-gray-600">
                <summary className="cursor-pointer font-bold outline-none focus:text-red-600">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-4 whitespace-pre-wrap overflow-auto">
                  {this.state.error && this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;